import React, { useState, useEffect } from 'react';
import { Search, Filter, MoreHorizontal, CheckCircle, XCircle, List, Users, ChevronDown, ChevronRight, Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import LeaveDetailsModal from './LeaveDetailsModal';
import api, { getImageUrl } from '../api';

export default function LeaveRequests() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useState('group'); // 'list' or 'group'
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/leaves');
      const data = Array.isArray(response.data) ? response.data : response.data.data || [];
      const transformed = data.map(leave => {
        const calcDays = () => {
          if (!leave.start_date || !leave.end_date) return 0;
          const s = new Date(leave.start_date);
          const e = new Date(leave.end_date);
          if (isNaN(s) || isNaN(e)) return 0;
          return Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
        };
        
        const userName = leave.user?.name || 'Unknown';
        const nameParts = userName.split(' ').filter(Boolean);
        const initials = nameParts.length > 1 
            ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() 
            : userName.substring(0, 2).toUpperCase();
            
        return {
          ...leave,
          name: userName,
          role: leave.user?.employee?.designation || 'N/A',
          type: leave.leave_type || 'N/A',
          startDate: leave.start_date,
          endDate: leave.end_date,
          days: leave.duration && leave.duration > 0 ? leave.duration : calcDays(),
          status: leave.status?.toUpperCase() || 'PENDING',
          avatar: getImageUrl(leave.user?.profile_photo_path) || null,
          initials: initials,
          totalHistory: leave.user_total_attempts || 0,
          monthHistory: leave.user_month_leaves || 0
        };
      });
      
      setLeaves(transformed);
    } catch (err) {
      console.error("Error fetching leaves:", err);
      setError('Failed to load leave requests.');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (name) => {
    setExpandedRows(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: 'Delete Request?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/leaves/${id}`);
        fetchLeaves();
        toast.success('Leave request deleted successfully');
      } catch (err) {
        toast.error('Failed to delete leave request.');
        console.error(err);
      }
    }
  };

  const filtered = leaves.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(searchTerm.toLowerCase()) || l.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group data by employee
  const groupedData = Object.values(filtered.reduce((acc, curr) => {
    if (!acc[curr.name]) {
      acc[curr.name] = {
        name: curr.name,
        role: curr.role,
        avatar: curr.avatar,
        initials: curr.initials,
        requests: [],
        totalRequests: 0,
        approvedCount: 0,
        rejectedCount: 0,
        overallStatus: 'PENDING'
      };
    }
    acc[curr.name].requests.push(curr);
    acc[curr.name].totalRequests += 1;
    if (curr.status === 'APPROVED') acc[curr.name].approvedCount += 1;
    if (curr.status === 'REJECTED') acc[curr.name].rejectedCount += 1;
    
    if (acc[curr.name].rejectedCount > 0) acc[curr.name].overallStatus = 'REJECTED';
    else if (acc[curr.name].approvedCount === acc[curr.name].totalRequests) acc[curr.name].overallStatus = 'APPROVED';
    
    return acc;
  }, {}));

  if (loading) return <div className="p-20 text-center text-slate-500 font-bold">Loading leave requests...</div>;
  if (error) return <div className="p-20 text-center text-red-500 font-bold">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Top Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3 lg:px-5 lg:py-3.5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Leave Requests</h1>
        </div>
        
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64 border border-gray-200 rounded-md bg-gray-50">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search by name or type..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-transparent text-sm focus:outline-none text-slate-700 font-medium placeholder:font-normal"
            />
          </div>
          <div className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm transition-colors hover:border-gray-300">
            <Filter size={15} className="text-slate-500" strokeWidth={2.5} />
            <span className="text-sm font-bold text-slate-600">Filter:</span>
            <div className="relative flex items-center">
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none bg-transparent text-sm font-black text-[#1e293b] pr-5 outline-none cursor-pointer focus:ring-0"
              >
                <option value="ALL">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <ChevronDown size={14} strokeWidth={3} className="text-[#1e293b] absolute right-0 pointer-events-none" />
            </div>
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-gray-100 p-1 rounded-md border border-gray-200">
            <button 
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-sm transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="List View"
            >
              <List size={18} strokeWidth={2.5} />
            </button>
            <button 
              onClick={() => setViewMode('group')}
              className={`p-1.5 rounded-sm transition-colors ${viewMode === 'group' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="Group View"
            >
              <Users size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        
        {viewMode === 'list' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-slate-100 text-[11px] uppercase font-black text-[#6082b6] tracking-wider whitespace-nowrap shadow-[0_1px_2px_-1px_rgba(0,0,0,0.05)]">
                  <th className="px-6 py-4">EMPLOYEE</th>
                  <th className="px-4 py-4">LEAVE TYPE</th>
                  <th className="px-4 py-4">DURATION</th>
                  <th className="px-4 py-4">HISTORY (USAGE)</th>
                  <th className="px-4 py-4">STATUS</th>
                  <th className="px-4 py-4 text-center">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((record, idx) => (
                  <tr key={idx} onClick={() => setSelectedRecord(record)} className="hover:bg-gray-50/50 transition-colors group cursor-pointer">
                    {/* EMPLOYEE */}
                    <td className="px-6 py-4 min-w-[200px]">
                      <div className="flex items-center gap-3">
                        {record.avatar ? (
                          <img src={record.avatar} alt={record.name} className="w-10 h-10 rounded-full object-cover border border-gray-200 shadow-sm" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[13px] font-black tracking-widest shadow-sm border border-emerald-200">
                            {record.initials}
                          </div>
                        )}
                        <div>
                          <div className="text-[13px] font-bold text-slate-800">{record.name}</div>
                          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{record.role}</div>
                        </div>
                      </div>
                    </td>

                    {/* LEAVE TYPE */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-block w-[105px] text-center px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-bold rounded shadow-sm">
                        {record.type}
                      </span>
                    </td>

                    {/* DURATION */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-[12px] font-bold text-slate-700 mb-0.5">
                        {record.startDate} - {record.endDate}
                      </div>
                      <div className="text-[11px] font-bold text-gray-400">
                        {record.days} Days
                      </div>
                    </td>

                    {/* HISTORY (USAGE) */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                          <span className="text-[11px] font-bold text-slate-500">Total:</span>
                          <span className="text-[11px] font-black text-slate-800">{record.totalHistory}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          <span className="text-[11px] font-bold text-slate-500">Month:</span>
                          <span className="text-[11px] font-black text-slate-800">{record.monthHistory}</span>
                        </div>
                      </div>
                    </td>

                    {/* STATUS */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {record.status === 'REJECTED' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 border border-red-100 text-red-600 text-[10px] font-black tracking-widest uppercase shadow-sm">
                          <XCircle size={12} strokeWidth={3} /> {record.status}
                        </span>
                      ) : record.status === 'APPROVED' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black tracking-widest uppercase shadow-sm">
                          <CheckCircle size={12} strokeWidth={3} /> {record.status}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-600 text-[10px] font-black tracking-widest uppercase shadow-sm">
                          <MoreHorizontal size={12} strokeWidth={3} /> {record.status}
                        </span>
                      )}
                    </td>

                    {/* ACTION */}
                    <td className="px-4 py-4 text-center">
                      <button 
                        onClick={(e) => handleDelete(e, record.id)}
                        className="text-gray-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors pointer-events-auto"
                        title="Delete Request"
                      >
                        <Trash2 size={18} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'group' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-slate-100 text-[11px] uppercase font-black text-[#6082b6] tracking-wider whitespace-nowrap shadow-[0_1px_2px_-1px_rgba(0,0,0,0.05)]">
                  <th className="w-12 px-4 py-4"></th>
                  <th className="px-4 py-4">EMPLOYEE</th>
                  <th className="px-4 py-4">REQUESTS SUMMARY</th>
                  <th className="px-4 py-4">STATUS</th>
                  <th className="px-4 py-4">APPROVED</th>
                  <th className="px-6 py-4 text-right">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {groupedData.map((group, idx) => {
                  const isExpanded = expandedRows[group.name];
                  
                  return (
                    <React.Fragment key={idx}>
                      {/* Parent Row */}
                      <tr 
                        onClick={() => toggleRow(group.name)}
                        className={`hover:bg-gray-50/50 transition-colors cursor-pointer group-row ${isExpanded ? 'bg-indigo-50/30' : ''}`}
                      >
                        {/* Expand Icon */}
                        <td className={`px-4 py-4 ${isExpanded ? 'border-l-4 border-l-indigo-500 bg-indigo-50/50' : 'border-l-4 border-l-transparent'}`}>
                          <button className="text-gray-400 hover:text-indigo-600 transition-colors p-1 rounded-full hover:bg-indigo-100/50">
                            {isExpanded ? <ChevronDown size={18} strokeWidth={2.5} className="text-indigo-500" /> : <ChevronRight size={18} strokeWidth={2.5} />}
                          </button>
                        </td>

                        {/* EMPLOYEE */}
                        <td className="px-4 py-4 min-w-[200px]">
                          <div className="flex items-center gap-3">
                            {group.avatar ? (
                              <img src={group.avatar} alt={group.name} className="w-9 h-9 rounded-full object-cover border border-gray-200 shadow-sm" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[11px] font-black tracking-widest shadow-sm border border-emerald-200">
                                {group.initials}
                              </div>
                            )}
                            <div>
                              <div className="text-[13px] font-bold text-slate-800">{group.name}</div>
                              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{group.role}</div>
                            </div>
                          </div>
                        </td>

                        {/* REQUESTS SUMMARY */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-[11px] font-bold text-slate-600 shadow-sm">
                            <CalendarIcon size={14} className="text-slate-400" />
                            {group.totalRequests} Requests
                          </div>
                        </td>

                        {/* STATUS */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          {group.overallStatus === 'REJECTED' ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 border border-red-100 text-red-600 text-[10px] font-black tracking-widest uppercase shadow-sm">
                              <XCircle size={12} strokeWidth={3} /> {group.overallStatus}
                            </span>
                          ) : group.overallStatus === 'APPROVED' ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black tracking-widest uppercase shadow-sm">
                              <CheckCircle size={12} strokeWidth={3} /> {group.overallStatus}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-600 text-[10px] font-black tracking-widest uppercase shadow-sm">
                              <MoreHorizontal size={12} strokeWidth={3} /> {group.overallStatus}
                            </span>
                          )}
                        </td>

                        {/* APPROVED count */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-[11px] font-black text-emerald-600">{group.approvedCount} Approved</span>
                        </td>

                        {/* ACTION */}
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <span className="text-[11px] font-bold text-gray-400 hover:text-indigo-600 uppercase tracking-wider transition-colors">
                            View Details
                          </span>
                        </td>
                      </tr>

                      {/* Nested Expanded Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="p-0 border-l-4 border-l-indigo-500 bg-slate-50/50">
                            <div className="pl-14 pr-6 py-4 bg-slate-50 ring-1 ring-inset ring-black/5 shadow-inner">
                              <table className="w-full text-left">
                                <thead>
                                  <tr className="bg-[#f1f5f9] text-[11px] font-black uppercase text-[#6082b6] tracking-wider border-b border-gray-200">
                                    <th className="pb-3 px-2">TYPE</th>
                                    <th className="pb-3 px-2">DATES</th>
                                    <th className="pb-3 px-2">DURATION</th>
                                    <th className="pb-3 px-2">STATUS</th>
                                    <th className="pb-3 px-2 text-right">ACTION</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {group.requests.map((req, ridx) => (
                                    <tr key={ridx} onClick={() => setSelectedRecord(req)} className="hover:bg-indigo-50/50 transition-colors group/nested cursor-pointer">
                                      <td className="py-3 px-2">
                                        <span className="inline-block w-[105px] text-center px-2.5 py-1 bg-white border border-gray-200 text-slate-600 text-[11px] font-black rounded shadow-sm">
                                          {req.type}
                                        </span>
                                      </td>
                                      <td className="py-3 px-2 text-[12px] font-bold text-slate-600">
                                        {req.startDate} - {req.endDate}
                                      </td>
                                      <td className="py-3 px-2 text-[12px] font-bold text-slate-600">
                                        {req.days} Days
                                      </td>
                                      <td className="py-3 px-2">
                                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                                          req.status === 'REJECTED' ? 'text-red-600 bg-red-50 border border-red-100' : 
                                          req.status === 'APPROVED' ? 'text-emerald-600 bg-emerald-50 border border-emerald-100' : 
                                          'text-amber-600 bg-amber-50 border border-amber-100'
                                        }`}>
                                          {req.status}
                                        </span>
                                      </td>
                                      <td className="py-3 px-2 text-right">
                                        <button 
                                          onClick={() => setSelectedRecord(req)}
                                          className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-100 hover:text-indigo-800 rounded transition-colors shadow-sm"
                                        >
                                          Review
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeaveDetailsModal 
        isOpen={!!selectedRecord} 
        onClose={() => setSelectedRecord(null)} 
        record={selectedRecord} 
        onUpdate={fetchLeaves}
      />
    </div>
  );
}
